import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, UploadSimple, ImageSquare, X, CheckCircle, WarningCircle, CircleNotch, ArrowSquareOut, Crown, FilmStrip, PencilSimple, Sparkle, FolderOpen } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { uploadLargeFile, formatBytes } from "../../lib/multipartUpload";
import { uploadImage } from "../../lib/uploadImage";
import { parseScenepackName, looksLikeScenepack, QUALITIES } from "../../lib/parseScenepackName";
import { collectDropped, filesFromInput, slugify, IMAGE_FILE } from "../../lib/dropped";
import { ImdbBadge, RtBadge } from "../RatingBadges";
import CreditPicker from "../CreditPicker";
import SmartImg from "../SmartImg";

const GENRES = ["Action", "Sci-Fi", "Drama", "Thriller", "Crime", "Animation", "Horror", "Romance", "Comedy", "Fantasy"];
const MAX_AT_ONCE = 2; // files uploading at the same time
const POSTER_WORDS = ["poster", "movie", "cover", "card", "show"];

const makeRow = ({ file, path }, lockCharacter = "") => {
  const p = parseScenepackName(file.name, path);
  return {
    id: crypto.randomUUID(),
    file,
    sizeLabel: formatBytes(file.size),
    character: lockCharacter || p.character, // "Add version" on a character: everything dropped belongs to them
    title: p.title, // a season / part, when the name has one
    quality: p.quality,
    premium: p.premium,
    nameMovie: p.movie, // the movie the FILE NAME says (checked against this card)
    notes: p.notes,
    up: "queued", // queued | uploading | done | error
    url: "",
    error: "",
    posted: false,
  };
};

// "Hawkeye" vs "Marvel's Hawkeye": close enough
const sameMovie = (a, b) => {
  const x = slugify(a);
  const y = slugify(b);
  return !x || !y || x === y || x.includes(y) || y.includes(x);
};

/** A text box that only commits on blur / Enter (so renaming doesn't regroup while typing). */
function RenameField({ value, onCommit }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { const v = draft.trim(); if (v && v !== value) onCommit(v); else setDraft(value); };
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="!bg-transparent !border-transparent hover:!border-white/15 focus:!border-[#FF8090]/60 !px-2 !py-1 !-ml-2 font-display font-black !text-xl !rounded-lg w-full"
      title="Click to rename"
    />
  );
}

export default function MovieWorkspace({ card: initialCard, initialFiles = [], lockCharacter = "", onBack, onDone, onPendingChange }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");

  const [card, setCard] = useState(initialCard);
  const isNew = card.mode === "new";
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({});
  const [speed, setSpeed] = useState({});
  const [existingChars, setExistingChars] = useState({}); // slug -> { name, image, packs }
  const [designs, setDesigns] = useState({}); // character slug -> design url (waiting to be published)
  const [loose, setLoose] = useState([]); // designs we couldn't match by name
  const [useImdbPoster, setUseImdbPoster] = useState(false);
  const [designBusy, setDesignBusy] = useState(0);
  const [creditUser, setCreditUser] = useState(null); // admins can credit an upload to any member (null = themselves)
  const creditTo = creditUser?.id || "";
  const [posting, setPosting] = useState(false);
  const [dragOver, setDragOver] = useState(null); // "files" | "designs" | null

  const controllers = useRef({});
  const lastTick = useRef({});
  const created = useRef(false); // the new movie card exists on the site once its first pack is posted
  const rowsRef = useRef([]);
  const didInit = useRef(false);
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  const designInput = useRef(null);
  const posterInput = useRef(null);
  const charInput = useRef(null);
  const charTarget = useRef("");

  const patchRow = useCallback((id, changes) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...changes } : r))), []);
  const patchCard = (changes) => setCard((c) => ({ ...c, ...changes }));
  const patchCardRef = useRef(patchCard);
  patchCardRef.current = patchCard;

  rowsRef.current = rows;

  // Tells the parent whether closing right now would lose work (a file that's
  // uploaded, or still uploading, but hasn't been published yet) — used to
  // confirm before the surrounding modal is allowed to close.
  const pending = rows.some((r) => !r.posted) || designBusy > 0 || posting;
  useEffect(() => { onPendingChange?.(pending); }, [pending, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  // "All movies" is a deliberate exit too — it throws away anything not yet
  // published the same way closing the window does, so it gets the same warning.
  const confirmedBack = () => {
    if (pending && !window.confirm("You have an uploaded scenepack that hasn't been published yet. Going back now deletes it. Continue?")) return;
    onBack();
  };

  // ---------- characters already on this card ----------
  useEffect(() => {
    if (isNew) return;
    api.get(`/movies/${card.slug}`).then((r) => {
      setExistingChars(Object.fromEntries((r.data.characters || []).map((c) => [c.slug, { name: (c.name || "").trim(), image: c.thumbnail_url, packs: c.packs_count }])));
    }).catch(() => {});
  }, [card.slug, isNew]);

  // ---------- groups: one per character in the dropped files ----------
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const slug = slugify(r.character) || "unnamed";
      if (!map.has(slug)) map.set(slug, { slug, name: existingChars[slug]?.name || r.character || "Unnamed character", rows: [] });
      map.get(slug).rows.push(r);
    }
    return [...map.values()];
  }, [rows, existingChars]);

  // A real character already on this card, used as the drop-zone example so the
  // naming rule is concrete instead of an abstract "Character".
  const exampleCharacterName = Object.values(existingChars)[0]?.name || "Character";

  const targetsRef = useRef({});
  targetsRef.current = { cardSlug: card.slug, charSlugs: [...new Set([...groups.map((g) => g.slug), ...Object.keys(existingChars)])] };

  // ---------- designs: poster + character images ----------
  const applyPoster = useCallback(async (url) => {
    if (isNew) { patchCardRef.current({ poster: url }); setUseImdbPoster(false); return; }
    try {
      await api.patch(`/movies/${card.slug}/design`, { poster_url: url });
      patchCardRef.current({ poster: url });
      toast.success("Poster design updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update the poster");
    }
  }, [isNew, card.slug]);

  const applyCharDesign = useCallback(async (slug, url) => {
    if (existingChars[slug]) {
      try {
        await api.patch(`/movies/${card.slug}/characters/${slug}/design`, { image_url: url });
        setExistingChars((m) => ({ ...m, [slug]: { ...m[slug], image: url } }));
        toast.success(`${existingChars[slug].name} design updated`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update the design");
      }
    } else {
      setDesigns((d) => ({ ...d, [slug]: url }));
    }
  }, [existingChars, card.slug]);

  const matchDesign = (base) => {
    const { cardSlug, charSlugs } = targetsRef.current;
    if (base === cardSlug || POSTER_WORDS.some((w) => base.includes(w))) return { type: "poster" };
    if (charSlugs.includes(base)) return { type: "char", slug: base };
    const near = charSlugs.find((s) => s.length >= 4 && (base.includes(s) || (base.length >= 4 && s.includes(base))));
    return near ? { type: "char", slug: near } : null;
  };

  const handleDesigns = useCallback(async (items) => {
    for (const { file } of items) {
      setDesignBusy((b) => b + 1);
      try {
        const url = await uploadImage(file);
        const target = matchDesign(slugify(file.name.replace(/\.[^.]+$/, "")));
        if (target?.type === "poster") await applyPoster(url);
        else if (target?.type === "char") await applyCharDesign(target.slug, url);
        else setLoose((l) => [...l, { id: crypto.randomUUID(), name: file.name, url }]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Could not upload ${file.name}`);
      }
      setDesignBusy((b) => b - 1);
    }
  }, [applyPoster, applyCharDesign]);

  const handlePosterFile = async (file) => {
    if (!file) return;
    setDesignBusy((b) => b + 1);
    try { await applyPoster(await uploadImage(file)); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the poster"); }
    setDesignBusy((b) => b - 1);
  };

  const assignLoose = async (item, value) => {
    if (value === "poster") await applyPoster(item.url);
    else await applyCharDesign(value, item.url);
    setLoose((l) => l.filter((x) => x.id !== item.id));
  };

  // ---------- adding files ----------
  const addItems = useCallback((items) => {
    const videos = items.filter((i) => looksLikeScenepack(i.file.name));
    const images = items.filter((i) => IMAGE_FILE.test(i.file.name));
    const skipped = items.length - videos.length - images.length;
    if (videos.length) setRows((rs) => [...rs, ...videos.map((v) => makeRow(v, lockCharacter))]);
    if (images.length) handleDesigns(images);
    if (skipped) toast(`Skipped ${skipped} file${skipped !== 1 ? "s" : ""} that aren't scenepacks or images`);
  }, [handleDesigns, lockCharacter]);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (initialFiles.length) addItems(initialFiles);
  }, [initialFiles, addItems]);

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(null);
    addItems(await collectDropped(e.dataTransfer));
  };

  // ---------- uploading, two files at a time ----------
  const begin = useCallback((row) => {
    if (controllers.current[row.id]) return;
    const ctl = new AbortController();
    controllers.current[row.id] = ctl;
    patchRow(row.id, { up: "uploading", error: "" });
    uploadLargeFile(row.file, {
      signal: ctl.signal,
      onProgress: (p, bps) => {
        const now = Date.now();
        if (p >= 1 || now - (lastTick.current[row.id] || 0) > 250) {
          lastTick.current[row.id] = now;
          setProgress((m) => ({ ...m, [row.id]: p }));
          setSpeed((m) => ({ ...m, [row.id]: bps }));
        }
      },
    })
      .then(({ url }) => patchRow(row.id, { up: "done", url }))
      .catch((err) => { if (err.name !== "AbortError") patchRow(row.id, { up: "error", error: err.message || "Upload failed" }); })
      .finally(() => { delete controllers.current[row.id]; });
  }, [patchRow]);

  useEffect(() => {
    if (rows.filter((r) => r.up === "uploading").length >= MAX_AT_ONCE) return;
    const next = rows.find((r) => r.up === "queued");
    if (next) begin(next);
  }, [rows, begin]);

  const discardFile = (row) => {
    const path = row.url.split("/api/files/")[1];
    if (path) api.post("/upload/discard", { path }).catch(() => {});
  };

  const removeRow = (row) => {
    controllers.current[row.id]?.abort();
    if (row.up === "done" && row.url && !row.posted) discardFile(row);
    setRows((rs) => rs.filter((r) => r.id !== row.id));
  };

  // closing the window without publishing: stop uploads and delete what was uploaded
  useEffect(() => () => {
    Object.values(controllers.current).forEach((c) => c.abort());
    rowsRef.current.filter((r) => r.up === "done" && r.url && !r.posted).forEach(discardFile);
  }, []);

  const busy = rows.some((r) => !r.posted && (r.up === "uploading" || r.up === "queued"));
  useEffect(() => {
    if (!busy && !posting) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy, posting]);

  // ---------- the new card's own details ----------
  const pickCandidate = async (imdbId) => {
    try {
      const { data } = await api.get("/movies/resolve", { params: { imdb: imdbId } });
      patchCard({
        slug: data.key, title: data.title, year: data.year || card.year, genre: data.genre || card.genre,
        imdb_url: data.imdb_url, rt_url: data.rt_url, rt_verified: data.rt_verified, imdb_rating: data.imdb_rating,
        rt_rating: data.rt_rating, type: data.type, suggestedPoster: data.poster_url, candidates: data.candidates, found: true,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load that title");
    }
  };

  const posterFinal = isNew ? card.poster || (useImdbPoster ? card.suggestedPoster : "") : card.poster;

  // ---------- checking + publishing ----------
  const resolved = rows.map((r) => {
    const problems = [];
    if (!r.character.trim()) problems.push("Add the character");
    if (!r.quality) problems.push("Pick the quality");
    return { ...r, problems, mismatch: r.nameMovie && !sameMovie(r.nameMovie, card.title) };
  });
  const open = resolved.filter((r) => !r.posted);
  const ready = open.filter((r) => r.up === "done" && r.problems.length === 0);
  const cardProblem = isNew && !posterFinal ? "Add your poster design for this movie card" : "";
  const canPublish = open.length > 0 && ready.length === open.length && !cardProblem && !posting && designBusy === 0;

  const publish = async () => {
    setPosting(true);
    let posted = 0;
    for (const r of open) {
      const slug = slugify(r.character);
      const movieExists = !isNew || created.current;
      const body = {
        character_name: existingChars[slug]?.name || groups.find((g) => g.slug === slug)?.name || r.character.trim(),
        title: r.title.trim() || undefined,
        quality: r.quality,
        premium: isAdmin ? r.premium : undefined,
        creator_id: isAdmin && creditTo && creditTo !== user?.id ? creditTo : undefined,
        download_url: r.url,
        file_size: r.sizeLabel,
        thumbnail_url: existingChars[slug] ? "" : designs[slug] || "",
        ...(movieExists
          ? { existing_movie_slug: card.slug }
          : card.celebrity
          ? { celebrity: true, person_id: card.person?.id, movie_thumbnail_url: posterFinal }
          : {
              movie_title: card.title,
              genre: card.genre || "Action",
              year: Number(card.year) || new Date().getFullYear(),
              movie_thumbnail_url: posterFinal,
              imdb_url: card.imdb_url,
              rt_url: card.rt_url,
            }),
      };
      try {
        await api.post("/scenepacks", body);
        created.current = true;
        patchRow(r.id, { posted: true, error: "" });
        posted += 1;
      } catch (err) {
        const message = formatApiError(err.response?.data?.detail) || "Could not publish";
        patchRow(r.id, { error: message });
        toast.error(`${r.file.name}: ${message}`);
        setPosting(false);
        return;
      }
    }
    toast.success(`${posted} scenepack${posted !== 1 ? "s" : ""} published${isNew ? ` — “${card.title}” is now on the site` : ""}`);
    setPosting(false);
    onDone?.();
  };

  const renameCharacter = (fromSlug, name) => setRows((rs) => rs.map((r) => (slugify(r.character) === fromSlug ? { ...r, character: name } : r)));

  const zoneClasses = (zone) =>
    `rounded-2xl border-2 border-dashed p-5 text-center transition cursor-default ${dragOver === zone ? "border-[#FF8090] bg-[#FF8090]/[0.07] scale-[1.01]" : "border-white/12 bg-white/[0.02] hover:border-white/25"}`;

  // =====================================================================
  return (
    <div data-testid="movie-workspace">
      <button type="button" onClick={confirmedBack} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-white/50 hover:text-white mb-5" data-testid="workspace-back">
        <ArrowLeft size={13} /> All movies
      </button>

      <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-8 items-start">
        {/* ================= the movie card ================= */}
        <aside className="lg:sticky lg:top-2 space-y-4" data-testid="workspace-card">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A] aspect-[2/3] max-w-[190px] sm:max-w-[240px] mx-auto lg:max-w-none shadow-[0_30px_80px_-30px_rgba(136,0,21,0.6)]">
            {posterFinal ? (
              <SmartImg src={posterFinal} width={640} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="workspace-poster" />
            ) : (
              <button type="button" onClick={() => posterInput.current?.click()} className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/40 hover:text-white/70 transition border-2 border-dashed border-white/15 m-3 rounded-xl" data-testid="workspace-poster-empty">
                <ImageSquare size={34} /> <span className="text-xs font-display font-bold px-4 text-center">Upload your poster design</span>
              </button>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent pointer-events-none" />
            {isNew && <div className="absolute top-3 left-3 chip chip-garnet">{card.celebrity ? "New celebrity" : "New card"}</div>}
            {posterFinal && (
              <button type="button" onClick={() => posterInput.current?.click()} title="Replace the poster design" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur hover:bg-[#880015] flex items-center justify-center transition" data-testid="workspace-poster-replace">
                <PencilSimple size={15} />
              </button>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
              <div className="font-display font-black text-2xl leading-tight">{card.title}</div>
              <div className="text-[11px] font-mono text-white/55 mt-1">{card.celebrity ? "Celebrity" : [card.year || "", card.type === "series" ? "TV series" : card.type === "movie" ? "Movie" : "", card.genre].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          <input ref={posterInput} type="file" accept="image/*" className="hidden" onChange={(e) => { handlePosterFile(e.target.files[0]); e.target.value = ""; }} data-testid="poster-input" />

          {(card.imdb_url || card.rt_url) && (
            <div className="flex items-center gap-4 px-1">
              {card.imdb_url && <a href={card.imdb_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:opacity-80" data-testid="workspace-imdb"><ImdbBadge size={16} />{card.imdb_rating > 0 && <span className="font-display font-bold text-sm">{Number(card.imdb_rating).toFixed(1)}</span>}<ArrowSquareOut size={11} className="text-white/40" /></a>}
              {card.rt_url && <a href={card.rt_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:opacity-80" data-testid="workspace-rt"><RtBadge size={18} />{card.rt_rating > 0 && <span className="font-display font-bold text-sm">{card.rt_rating}%</span>}<ArrowSquareOut size={11} className="text-white/40" /></a>}
            </div>
          )}

          {isNew && card.celebrity ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2 text-left" data-testid="workspace-celebrity-details">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090]">Celebrity card</div>
              <p className="text-[11px] text-white/45 leading-relaxed">Their photo is the card's poster — replace it above with your own design if you like. Name each file for what it is (“Interviews”, “Red carpet”…). The card goes live when you publish its first scenepack.</p>
            </div>
          ) : isNew ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3 text-left" data-testid="workspace-card-details">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090]">Card details</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label>Genre</label>
                  <select value={card.genre} onChange={(e) => patchCard({ genre: e.target.value })} data-testid="workspace-genre">{GENRES.map((g) => <option key={g} value={g}>{g}</option>)}</select>
                </div>
                <div>
                  <label>Year</label>
                  <input type="number" value={card.year || ""} onChange={(e) => patchCard({ year: e.target.value })} />
                </div>
              </div>
              <div><label>IMDb link</label><input value={card.imdb_url} onChange={(e) => patchCard({ imdb_url: e.target.value })} placeholder="https://www.imdb.com/title/…" data-testid="workspace-imdb-input" /></div>
              <div>
                <label>Rotten Tomatoes link {card.rt_url && <span className={`normal-case tracking-normal ${card.rt_verified ? "text-[#7FE7A5]" : "text-[#FFB4BE]"}`}>· {card.rt_verified ? "found" : "check it"}</span>}</label>
                <input value={card.rt_url} onChange={(e) => patchCard({ rt_url: e.target.value })} placeholder="https://www.rottentomatoes.com/…" data-testid="workspace-rt-input" />
              </div>
              {card.candidates?.length > 0 && (
                <select value="" onChange={(e) => e.target.value && pickCandidate(e.target.value)} className="!py-1.5 !text-xs" data-testid="workspace-candidates">
                  <option value="">Not the right movie? Pick another…</option>
                  {card.candidates.map((c) => <option key={c.imdb_id} value={c.imdb_id}>{c.title} ({c.year}) — {c.type === "series" ? "TV series" : c.type}</option>)}
                </select>
              )}
              {!card.poster && card.suggestedPoster && (
                <button type="button" onClick={() => setUseImdbPoster((v) => !v)} className={`w-full text-[11px] font-mono uppercase tracking-wider py-2 rounded-lg border transition ${useImdbPoster ? "border-[#FF8090] text-[#FF8090]" : "border-white/10 text-white/45 hover:text-white"}`} data-testid="workspace-use-imdb-poster">
                  {useImdbPoster ? "Using the IMDb poster for now ✓" : "No design yet? Use the IMDb poster for now"}
                </button>
              )}
              <p className="text-[10px] text-white/35 leading-relaxed">This card goes live when you publish its first scenepack.</p>
            </div>
          ) : (
            <p className="text-[11px] text-white/40 leading-relaxed px-1">This card is already on the site: {card.characters} character{card.characters !== 1 ? "s" : ""}, {card.packs} scenepack{card.packs !== 1 ? "s" : ""}. What you add joins it.</p>
          )}
        </aside>

        {/* ================= inside the card ================= */}
        <section className="space-y-6 min-w-0">
          {lockCharacter && (
            <div className="rounded-xl border border-[#880015]/50 bg-[#880015]/10 px-4 py-3 text-sm flex items-center gap-2" data-testid="locked-character-banner">
              <Sparkle size={15} weight="fill" className="text-[#FF8090] flex-shrink-0" />
              <span>Adding versions to <b className="font-display">{lockCharacter}</b> — whatever you drop below joins them.</span>
            </div>
          )}
          {isAdmin && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="credit-to">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090]">Admin</div>
              <label className="!mb-0 text-xs text-white/60 normal-case tracking-normal">Credit these uploads to</label>
              <CreditPicker me={user} value={creditUser} onChange={(m) => setCreditUser(m?.id === user?.id ? null : m)} testId="credit-to-picker" />
              {creditTo && <span className="text-[11px] text-white/45 w-full">It will show on their profile, and they can edit or delete it.</span>}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4" data-testid="import-zones">
            <div onDragOver={(e) => { e.preventDefault(); setDragOver("files"); }} onDragLeave={() => setDragOver(null)} onDrop={onDrop} className={zoneClasses("files")} data-testid="zone-scenepacks">
              <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#880015]/25 flex items-center justify-center"><FilmStrip size={24} className="text-[#FF8090]" /></div>
              <div className="font-display font-bold mb-1">Scenepacks</div>
              <div className="text-[11px] text-white/45 mb-1 leading-relaxed">Drop the video files.</div>
              <div className="text-[10px] font-mono text-[#FF8090]/90 mb-3 break-words">{exampleCharacterName} - {card.title} 4K REMUX @you</div>
              {!isNew && exampleCharacterName !== "Character" && (
                <div className="text-[10px] text-white/35 mb-3 -mt-2 leading-relaxed">
                  Start the name with <b className="text-white/55">{exampleCharacterName}</b> to add another version to them — a different name starts a new character.
                </div>
              )}
              <div className="flex justify-center gap-2 flex-wrap">
                <button type="button" onClick={() => fileInput.current?.click()} className="btn-secondary !py-2 !px-3.5 !text-[11px]" data-testid="choose-scenepacks"><UploadSimple size={13} /> Choose files</button>
                <button type="button" onClick={() => folderInput.current?.click()} className="btn-secondary !py-2 !px-3.5 !text-[11px]" data-testid="choose-folder"><FolderOpen size={13} /> Folder</button>
              </div>
              <input ref={fileInput} type="file" multiple onChange={(e) => addItems(filesFromInput(e))} className="hidden" data-testid="file-input-scenepacks" />
              <input ref={folderInput} type="file" multiple webkitdirectory="" directory="" onChange={(e) => addItems(filesFromInput(e))} className="hidden" />
            </div>

            <div onDragOver={(e) => { e.preventDefault(); setDragOver("designs"); }} onDragLeave={() => setDragOver(null)} onDrop={onDrop} className={zoneClasses("designs")} data-testid="zone-designs">
              <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#880015]/25 flex items-center justify-center">{designBusy > 0 ? <CircleNotch size={24} className="text-[#FF8090] animate-spin" /> : <ImageSquare size={24} className="text-[#FF8090]" />}</div>
              <div className="font-display font-bold mb-1">Posters & designs</div>
              <div className="text-[11px] text-white/45 mb-1 leading-relaxed">Your movie poster and character designs.</div>
              <div className="text-[10px] font-mono text-[#FF8090]/90 mb-3">Name it after the character: Yelena Romanoff.png</div>
              <button type="button" onClick={() => designInput.current?.click()} className="btn-secondary !py-2 !px-3.5 !text-[11px]" data-testid="choose-designs"><ImageSquare size={13} /> Choose images</button>
              <input ref={designInput} type="file" multiple accept="image/*" onChange={(e) => handleDesigns(filesFromInput(e))} className="hidden" data-testid="file-input-designs" />
            </div>
          </div>

          {loose.length > 0 && (
            <div className="rounded-xl border border-[#FFB4BE]/30 bg-[#880015]/[0.06] p-4 space-y-3" data-testid="loose-designs">
              <div className="text-xs text-white/70">These designs couldn't be matched by name — say where each one goes:</div>
              {loose.map((d) => (
                <div key={d.id} className="flex items-center gap-3">
                  <img src={d.url} alt="" className="w-12 h-12 rounded-md object-cover border border-white/10" />
                  <div className="text-[11px] font-mono text-white/55 truncate flex-1">{d.name}</div>
                  <select value="" onChange={(e) => e.target.value && assignLoose(d, e.target.value)} className="!py-1.5 !text-xs !w-auto">
                    <option value="">Use for…</option>
                    <option value="poster">The movie poster</option>
                    {targetsRef.current.charSlugs.map((s) => <option key={s} value={s}>{existingChars[s]?.name || groups.find((g) => g.slug === s)?.name || s}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}

          {groups.length === 0 ? (
            <div className="rounded-2xl border border-white/8 bg-white/[0.015] py-14 text-center" data-testid="workspace-empty">
              <div className="font-display font-bold text-lg mb-1">Drop your first scenepack</div>
              <div className="text-xs text-white/40 max-w-sm mx-auto">Each character gets its own card here, with a row for every version. The quality and size are read from the file.</div>
            </div>
          ) : (
            <div className="space-y-5" data-testid="workspace-characters">
              <AnimatePresence initial={false}>
                {groups.map((g) => {
                  const design = designs[g.slug] || existingChars[g.slug]?.image || "";
                  const onSite = !!existingChars[g.slug];
                  return (
                    <motion.div key={g.slug} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-2xl border border-white/10 bg-[#141414] overflow-hidden" data-testid={`char-card-${g.slug}`}>
                      <div className="flex gap-4 p-4 border-b border-white/5 bg-gradient-to-r from-[#880015]/10 to-transparent">
                        <div className="relative w-20 sm:w-24 aspect-[3/4] rounded-xl overflow-hidden bg-[#232323] border border-white/10 flex-shrink-0 group">
                          {design ? <SmartImg src={design} width={320} alt="" className="absolute inset-0 w-full h-full object-cover" /> : posterFinal ? <SmartImg src={posterFinal} width={320} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" /> : null}
                          <button type="button" onClick={() => { charTarget.current = g.slug; charInput.current?.click(); }} className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 group-hover:opacity-100 transition" title="Upload this character's design" data-testid={`char-design-${g.slug}`}>
                            <ImageSquare size={20} />
                          </button>
                        </div>
                        <div className="min-w-0 flex-1 flex flex-col justify-center gap-1.5">
                          <RenameField value={g.name} onCommit={(v) => renameCharacter(g.slug, v)} />
                          <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-wider">
                            <span className={onSite ? "text-white/45" : "text-[#FF8090]"}>{onSite ? `On the site · ${existingChars[g.slug].packs} pack${existingChars[g.slug].packs !== 1 ? "s" : ""}` : "New character"}</span>
                            <span className="text-white/25">·</span>
                            <span className="text-white/45">{g.rows.length} new version{g.rows.length !== 1 ? "s" : ""}</span>
                            {!design && <span className="text-[#FFB4BE] normal-case tracking-normal">· using the movie poster until you add a design</span>}
                          </div>
                          {!onSite && Object.keys(existingChars).length > 0 && (
                            <select
                              value=""
                              onChange={(e) => e.target.value && renameCharacter(g.slug, existingChars[e.target.value].name)}
                              className="!py-1 !text-[10px] !w-auto mt-0.5 normal-case tracking-normal"
                              data-testid={`merge-into-${g.slug}`}
                              title="Is this actually one of your existing characters? Pick it here instead of publishing a duplicate."
                            >
                              <option value="">Not a new character? Merge into…</option>
                              {Object.entries(existingChars).map(([slug, c]) => (
                                <option key={slug} value={slug}>{c.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>

                      <div className="divide-y divide-white/5">
                        <AnimatePresence initial={false}>
                          {resolved.filter((r) => slugify(r.character) === g.slug || (!slugify(r.character) && g.slug === "unnamed")).map((r) => {
                            const pct = Math.round((progress[r.id] || 0) * 100);
                            return (
                              <motion.div key={r.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, height: 0 }} className="p-4 space-y-3" data-testid={`row-${r.file.name}`}>
                                <div className="flex items-center gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="text-[12px] font-mono text-white/75 truncate" title={r.file.name}>{r.file.name}</div>
                                    <div className="text-[11px] font-mono text-white/40 mt-0.5">{r.sizeLabel}</div>
                                  </div>
                                  <div className="text-[11px] font-mono flex items-center gap-1.5 flex-shrink-0">
                                    {r.posted ? <span className="text-[#7FE7A5] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Published</span>
                                      : r.up === "done" ? <span className="text-[#7FE7A5] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Uploaded</span>
                                      : r.up === "error" ? <span className="text-[#FF4060]">Failed</span>
                                      : r.up === "uploading" ? <span className="text-white/65 flex items-center gap-1"><CircleNotch size={14} className="animate-spin" /> {pct}%{speed[r.id] > 0 ? ` · ${(speed[r.id] / 1024 ** 2).toFixed(1)} MB/s` : ""}</span>
                                      : <span className="text-white/35">Waiting…</span>}
                                  </div>
                                  {!r.posted && <button type="button" onClick={() => removeRow(r)} title="Remove" data-testid={`remove-${r.file.name}`} className="w-7 h-7 rounded-md border border-white/10 hover:border-[#880015] hover:bg-[#880015] text-white/50 hover:text-white flex items-center justify-center"><X size={12} weight="bold" /></button>}
                                </div>
                                {r.up === "uploading" && <div className="h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#880015] to-[#FF8090] transition-[width] duration-200" style={{ width: `${pct}%` }} /></div>}

                                {!r.posted && (
                                  <>
                                    <div className="flex flex-wrap items-center gap-3">
                                      <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" data-testid={`quality-${r.file.name}`}>
                                        {QUALITIES.map((q) => (
                                          <button key={q} type="button" onClick={() => patchRow(r.id, { quality: q })} className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider transition ${r.quality === q ? "bg-[#880015] text-white" : "text-white/50 hover:text-white hover:bg-white/5"}`}>{q}</button>
                                        ))}
                                      </div>
                                      <input value={r.title} onChange={(e) => patchRow(r.id, { title: e.target.value })} placeholder="Name (optional) — e.g. Season 1" className="!py-1.5 !text-xs flex-1 min-w-[160px]" data-testid={`title-${r.file.name}`} />
                                      {isAdmin && (
                                        <label className="flex items-center gap-1.5 !mb-0 text-[11px] text-white/60 cursor-pointer select-none normal-case tracking-normal">
                                          <input type="checkbox" checked={r.premium} onChange={(e) => patchRow(r.id, { premium: e.target.checked })} className="!w-auto" /> <Crown size={11} weight="fill" className="text-[#FF8090]" /> AA+
                                        </label>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                                      {r.mismatch && <span className="text-[#FFB4BE]">The file name says “{r.nameMovie}” — this card is “{card.title}”</span>}
                                      {r.notes.map((n) => <span key={n} className="text-white/45">{n}</span>)}
                                      {r.problems.map((p) => <span key={p} className="text-[#FFB4BE] flex items-center gap-1"><WarningCircle size={11} /> {p}</span>)}
                                      {r.error && <span className="text-[#FF4060]">{r.error}</span>}
                                      {r.up === "error" && <button type="button" onClick={() => patchRow(r.id, { up: "queued" })} className="text-[#FF8090] hover:underline">Try again</button>}
                                    </div>
                                  </>
                                )}
                              </motion.div>
                            );
                          })}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {!isNew && Object.keys(existingChars).length > 0 && (
            <details className="rounded-xl border border-white/10 bg-white/[0.015] p-4" data-testid="existing-characters">
              <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-wider text-white/45 hover:text-white/75 list-none">Characters already on this card ({Object.keys(existingChars).length}) — change a design</summary>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {Object.entries(existingChars).map(([slug, c]) => (
                  <button key={slug} type="button" onClick={() => { charTarget.current = slug; charInput.current?.click(); }} className="group text-left rounded-xl border border-white/10 overflow-hidden hover:border-[#FF8090]/50 transition">
                    <div className="relative aspect-[4/3] bg-[#232323]">{c.image && <SmartImg src={c.image} width={320} alt="" className="absolute inset-0 w-full h-full object-cover" />}<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"><ImageSquare size={20} /></div></div>
                    <div className="p-2 text-xs font-display font-bold truncate">{c.name}</div>
                  </button>
                ))}
              </div>
            </details>
          )}
          <input ref={charInput} type="file" accept="image/*" className="hidden" data-testid="char-design-input"
            onChange={async (e) => {
              const file = e.target.files[0]; e.target.value = "";
              if (!file) return;
              setDesignBusy((b) => b + 1);
              try { await applyCharDesign(charTarget.current, await uploadImage(file)); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the design"); }
              setDesignBusy((b) => b - 1);
            }} />
        </section>
      </div>

      {/* ================= publish bar ================= */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-6 md:-mx-8 mt-8 px-4 sm:px-6 md:px-8 py-4 bg-[#141414]/90 backdrop-blur-xl border-t border-white/10 flex items-center justify-between gap-4 flex-wrap z-10" data-testid="publish-bar">
        <div className="text-xs text-white/55" data-testid="publish-summary">
          {open.length === 0 ? "Add your first scenepack to continue." : (
            <>
              <b className="text-white">{ready.length}</b> of {open.length} ready
              {open.some((r) => r.up === "uploading" || r.up === "queued") && <span> · uploading…</span>}
              {cardProblem && <span className="text-[#FFB4BE]"> · {cardProblem}</span>}
            </>
          )}
        </div>
        <button type="button" onClick={publish} disabled={!canPublish} className="btn-primary disabled:opacity-40" data-testid="publish">
          {posting ? "Publishing…" : <><Sparkle size={15} weight="fill" /> Publish {open.length || ""} scenepack{open.length !== 1 ? "s" : ""}</>}
        </button>
      </div>
    </div>
  );
}
